/**
 * InvestorValueCalculator
 *
 * A small inline calculator that helps prospects on `/for-investors`
 * personalize the upgrade math: deals reviewed/month × hours saved
 * per deal × hourly rate = estimated monthly value of using the
 * platform. It also auto-recommends a tier based on monthly volume,
 * with a primary CTA to start that tier (or the "Lite Annual" save)
 * and a secondary "View {tier} dashboard" preview link.
 *
 * Designed to be calm, not gamified — slider tone, not a casino.
 * Numbers update live; persists last inputs to localStorage so a
 * returning visitor sees their own math.
 *
 * Telemetry (consent-gated):
 *   - investor_value_calculator_viewed (once when in viewport)
 *   - investor_value_calculator_input_changed { field, value }   (debounced)
 *   - investor_value_calculator_cta_clicked { tier, kind }
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, Calculator, Eye, Sparkles } from "lucide-react";
import { track } from "@/lib/analytics";

const STORE_KEY = "bl_investor_value_calc_v1";

// Tier ladder is intentionally simple. Tweak thresholds here if pricing
// or positioning shifts — single source of truth for the recommender.
const TIER_LADDER = [
    {
        id: "lite",
        plan_id: "investor_lite",
        name: "Investor Lite",
        price: 29,
        min_deals: 0,
        max_deals: 3,
        positioning: "Occasional deal review",
    },
    {
        id: "pro",
        plan_id: "investor_pro",
        name: "Investor Pro",
        price: 79,
        min_deals: 4,
        max_deals: 12,
        positioning: "Active investors with steady pipeline",
        badge: "Most Popular",
    },
    {
        id: "plus",
        plan_id: "investor_plus",
        name: "Investor Plus",
        price: 149,
        min_deals: 13,
        max_deals: 999,
        positioning: "Portfolio-scale workflow",
    },
];

function recommendTier(dealsPerMonth) {
    return (
        TIER_LADDER.find(
            (t) => dealsPerMonth >= t.min_deals && dealsPerMonth <= t.max_deals,
        ) || TIER_LADDER[0]
    );
}

function fmtUsd(n, fractionDigits = 0) {
    if (!Number.isFinite(n)) return "$0";
    return (
        "$" +
        Math.round(n).toLocaleString(undefined, {
            minimumFractionDigits: fractionDigits,
            maximumFractionDigits: fractionDigits,
        })
    );
}

function loadDefaults() {
    try {
        const raw = localStorage.getItem(STORE_KEY);
        if (!raw) return null;
        const v = JSON.parse(raw);
        if (
            Number.isFinite(v.deals) &&
            Number.isFinite(v.hours) &&
            Number.isFinite(v.rate)
        ) {
            return v;
        }
    } catch { /* non-critical */ }
    return null;
}

function SliderInput({
    label,
    suffix,
    min,
    max,
    step = 1,
    value,
    onChange,
    testid,
    hint,
}) {
    return (
        <label className="block" data-testid={testid}>
            <div className="flex items-baseline justify-between gap-2 mb-1.5">
                <span className="text-[11px] tracking-[0.16em] uppercase text-bl-muted">
                    {label}
                </span>
                <span
                    className="font-mono-bl text-bl text-sm"
                    data-testid={`${testid}-value`}
                >
                    {value}
                    {suffix}
                </span>
            </div>
            <input
                type="range"
                min={min}
                max={max}
                step={step}
                value={value}
                onChange={(e) => onChange(Number(e.target.value))}
                className="w-full h-1.5 rounded-full appearance-none bg-bl-bg cursor-pointer accent-bl-primary"
                aria-label={label}
                data-testid={`${testid}-input`}
            />
            {hint && (
                <div className="mt-1 text-[11px] text-bl-muted/80">{hint}</div>
            )}
        </label>
    );
}

export default function InvestorValueCalculator({ onPreview }) {
    const navigate = useNavigate();
    const defaults = loadDefaults();
    const [deals, setDeals] = useState(defaults?.deals ?? 4);
    const [hours, setHours] = useState(defaults?.hours ?? 4);
    const [rate, setRate] = useState(defaults?.rate ?? 75);

    const wrapRef = useRef(null);
    const shownRef = useRef(false);
    const trackTimerRef = useRef(null);

    // Persist + debounced telemetry on every change.
    useEffect(() => {
        try {
            localStorage.setItem(
                STORE_KEY,
                JSON.stringify({ deals, hours, rate }),
            );
        } catch { /* non-critical */ }
        if (trackTimerRef.current) clearTimeout(trackTimerRef.current);
        trackTimerRef.current = setTimeout(() => {
            track("investor_value_calculator_input_changed", {
                deals,
                hours,
                rate,
            });
        }, 700);
        return () => {
            if (trackTimerRef.current) clearTimeout(trackTimerRef.current);
        };
    }, [deals, hours, rate]);

    // One-shot "viewed" telemetry.
    useEffect(() => {
        if (!wrapRef.current) return undefined;
        if (typeof IntersectionObserver === "undefined") return undefined;
        const node = wrapRef.current;
        const io = new IntersectionObserver(
            (entries) => {
                for (const e of entries) {
                    if (e.isIntersecting && !shownRef.current) {
                        shownRef.current = true;
                        track("investor_value_calculator_viewed", {});
                        io.disconnect();
                    }
                }
            },
            { threshold: 0.3 },
        );
        io.observe(node);
        return () => io.disconnect();
    }, []);

    const monthlyValue = deals * hours * rate;
    const recommended = recommendTier(deals);
    const net = monthlyValue - recommended.price;

    const handleStart = () => {
        track("investor_value_calculator_cta_clicked", {
            tier: recommended.id,
            kind: "start_tier",
        });
        navigate("/pricing#investor-plans");
    };

    const handlePreview = () => {
        track("investor_value_calculator_cta_clicked", {
            tier: recommended.id,
            kind: "preview_dashboard",
        });
        if (typeof onPreview === "function") {
            onPreview(recommended.id);
        } else {
            navigate(`/for-investors?preview=${recommended.id}`);
        }
    };

    return (
        <section
            ref={wrapRef}
            className="bl-container mt-14 md:mt-16"
            data-testid="for-investors-value-calculator"
            data-reveal
        >
            <div className="bl-card p-6 sm:p-8 md:p-10 bg-bl-teal-tint/20 border-bl-primary/15">
                <div className="grid md:grid-cols-12 gap-6 md:gap-10 items-start">
                    {/* Header + inputs */}
                    <div className="md:col-span-7 min-w-0">
                        <div className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase text-bl-primary font-medium">
                            <Calculator className="w-3.5 h-3.5" />
                            Quick math · estimate only
                        </div>
                        <h3 className="mt-2 font-display font-semibold text-2xl sm:text-[1.75rem] text-bl tracking-tight">
                            Calculate your{" "}
                            <span className="font-italic-accent text-bl-primary">
                                monthly value.
                            </span>
                        </h3>
                        <p className="mt-2.5 text-bl-muted text-sm sm:text-[15px] leading-relaxed max-w-xl">
                            A rough sense of how a subscription pays off
                            against the time you save reviewing inspection
                            reports. These are planning ranges — not a
                            guarantee or contractor bid.
                        </p>

                        <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5">
                            <SliderInput
                                label="Deals reviewed / month"
                                suffix=""
                                min={1}
                                max={30}
                                value={deals}
                                onChange={setDeals}
                                testid="value-calc-deals"
                                hint="Inspection reports per month"
                            />
                            <SliderInput
                                label="Hours saved / deal"
                                suffix=" hrs"
                                min={1}
                                max={15}
                                value={hours}
                                onChange={setHours}
                                testid="value-calc-hours"
                                hint="Reading + organizing findings"
                            />
                            <SliderInput
                                label="Your hourly rate"
                                suffix=" /hr"
                                min={25}
                                max={500}
                                step={5}
                                value={rate}
                                onChange={setRate}
                                testid="value-calc-rate"
                                hint="What your time is worth"
                            />
                        </div>
                    </div>

                    {/* Result panel */}
                    <div
                        className="md:col-span-5 bl-card p-5 sm:p-6 bg-bl-surface border-bl-primary/30 self-stretch flex flex-col"
                        data-testid="value-calc-result"
                    >
                        <div className="text-[10px] tracking-[0.18em] uppercase text-bl-muted">
                            Estimated monthly value
                        </div>
                        <div
                            className="mt-1 font-display font-semibold text-bl text-[2.25rem] sm:text-[2.75rem] leading-none tracking-tight"
                            data-testid="value-calc-monthly-value"
                        >
                            {fmtUsd(monthlyValue)}
                            <span className="text-bl-muted text-sm sm:text-base font-mono-bl font-normal ml-1">
                                /mo
                            </span>
                        </div>
                        <div className="mt-1 text-[12px] text-bl-muted font-mono-bl">
                            {deals} deals × {hours} hrs ×{" "}
                            {fmtUsd(rate)}/hr
                        </div>

                        <div className="mt-5 pt-4 border-t border-bl">
                            <div className="text-[10px] tracking-[0.18em] uppercase text-bl-muted">
                                Recommended tier
                            </div>
                            <div className="mt-1 flex items-center gap-2 flex-wrap">
                                <div
                                    className="font-display font-semibold text-bl text-lg"
                                    data-testid="value-calc-recommended-tier"
                                >
                                    {recommended.name}
                                </div>
                                {recommended.badge && (
                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-bl-primary text-bl-surface text-[9px] tracking-[0.16em] uppercase font-medium">
                                        <Sparkles className="w-2.5 h-2.5" />
                                        {recommended.badge}
                                    </span>
                                )}
                            </div>
                            <div className="mt-0.5 text-[12px] text-bl-muted font-mono-bl">
                                {fmtUsd(recommended.price)}/mo ·{" "}
                                {recommended.positioning}
                            </div>
                        </div>

                        <div className="mt-4 flex items-baseline justify-between gap-2">
                            <span className="text-[11px] tracking-[0.16em] uppercase text-bl-muted">
                                Net monthly value
                            </span>
                            <span
                                className={
                                    "font-mono-bl text-base " +
                                    (net > 0 ? "text-bl-primary" : "text-bl")
                                }
                                data-testid="value-calc-net"
                            >
                                {net > 0 ? "+" : ""}
                                {fmtUsd(net)}
                            </span>
                        </div>

                        <div className="mt-5 flex flex-col gap-2">
                            <button
                                type="button"
                                onClick={handleStart}
                                className="bl-btn-primary w-full"
                                data-testid="value-calc-cta-start"
                            >
                                Start {recommended.name}
                                <ArrowUpRight className="w-4 h-4" />
                            </button>
                            <button
                                type="button"
                                onClick={handlePreview}
                                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium text-bl-primary bg-bl-teal-tint hover:bg-bl-teal-tint/70 border border-bl-primary/15 transition-colors min-h-[44px]"
                                data-testid="value-calc-cta-preview"
                            >
                                <Eye className="w-4 h-4" />
                                View {recommended.name} Dashboard
                            </button>
                            <div className="text-[10px] text-bl-muted/80 mt-1 leading-relaxed">
                                Estimates use the inputs above × your hourly
                                rate. Real-world savings vary by report
                                length, complexity, and how often you review
                                deals.
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
